import { useApolloClient } from "@apollo/client";
import { assertNever } from "@msys/common";
import type { TFunction } from "@msys/tolgee";
import { CardContainer, useFormatting } from "@msys/ui";
import {
  AttachFile as AttachFileIcon,
  MoveToInbox as MoveToInboxIcon,
} from "@mui/icons-material";
import {
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import moment from "moment";
import { useSnackbar } from "notistack";
import React from "react";
import { useNavigate } from "react-router-dom";
import { DocType } from "../../../../clients/graphqlTypes.js";
import { Avatar } from "../../../commons/Avatar.js";
import { CrmCreateFormPrefill } from "../../../features/crm/CrmCreateForm.js";
import { CreateProjectModal } from "../../../features/projects/modals/CreateProjectModal.js";
import { PendingDocSharePreviewModal } from "./PendingDocSharePreviewModal.js";
import {
  PendingDocSharesBox_PendingDocShareFragment,
  usePendingDocSharesBox_AcceptDocShareMutation,
  usePendingDocSharesBox_PendingDocSharesQuery,
} from "./PendingDocSharesBox.generated.js";

// Mounting is gated by `quote.document.update` from the outside (see Dashboard), so the
// query below only runs for permitted users; the box owns its own fetching and
// renders nothing when there are no pending shares.
export function PendingDocSharesBox() {
  const { t } = useTranslate(["Dashboard", "Global"]);
  const client = useApolloClient();
  const navigate = useNavigate();

  const pendingDocSharesQuery = usePendingDocSharesBox_PendingDocSharesQuery({
    client,
  });
  const pendingDocSharesResult = pendingDocSharesQuery.data?.pendingDocShares;
  const pendingDocShares =
    pendingDocSharesResult?.__typename === "PendingDocSharesResult"
      ? pendingDocSharesResult.pendingDocShares
      : [];

  const [acceptDocShare] = usePendingDocSharesBox_AcceptDocShareMutation({
    client,
  });

  const [acceptingShare, setAcceptingShare] =
    React.useState<PendingDocSharesBox_PendingDocShareFragment | null>(null);
  const [previewingShare, setPreviewingShare] =
    React.useState<PendingDocSharesBox_PendingDocShareFragment | null>(null);

  if (pendingDocShares.length === 0) {
    return null;
  }

  return (
    <CardContainer
      id="pending-doc-shares-box"
      isExpandable={false}
      itemCount={pendingDocShares.length}
      Icon={<MoveToInboxIcon color="secondary" />}
      title={t("Inbox", { ns: "Dashboard" })}
    >
      <Stack spacing={1} sx={{ p: 1 }}>
        {pendingDocShares.map(pendingDocShare => (
          <PendingDocShareCard
            key={pendingDocShare.id}
            pendingDocShare={pendingDocShare}
            onPreview={() => setPreviewingShare(pendingDocShare)}
            onAccept={() => setAcceptingShare(pendingDocShare)}
          />
        ))}
      </Stack>

      {previewingShare && (
        <PendingDocSharePreviewModal
          pendingDocShareId={previewingShare.id}
          handleClose={() => setPreviewingShare(null)}
          onAccept={() => setAcceptingShare(previewingShare)}
        />
      )}

      {acceptingShare && (
        <AcceptPendingDocShareModal
          pendingDocShare={acceptingShare}
          handleClose={() => setAcceptingShare(null)}
          handleAccepted={async (projectId, clientCrmOrganisationId) => {
            await acceptDocShare({
              variables: {
                input: {
                  id: acceptingShare.id,
                  acceptInProjectId: projectId,
                  // Only forward a CRM mapping when the share actually has a
                  // CLIENT actor to map; otherwise the backend would run a
                  // no-op CRM lookup/validation.
                  clientCrmOrganisationId: acceptingShare.clientActor
                    ? clientCrmOrganisationId
                    : null,
                },
              },
            });
            // Navigate to the freshly created project. This also unmounts the
            // dashboard (and the preview modal, when accepting from it), so the
            // now-deleted pending share is never re-queried — avoiding a
            // "pending doc share not found" error from a stale refetch.
            navigate(`/projects/${projectId}/overview`);
          }}
        />
      )}
    </CardContainer>
  );
}

function PendingDocShareCard({
  pendingDocShare,
  onPreview,
  onAccept,
}: {
  pendingDocShare: PendingDocSharesBox_PendingDocShareFragment;
  onPreview: () => void;
  onAccept: () => void;
}) {
  const { t } = useTranslate(["Dashboard", "Global"]);
  const { getFormattedPrice } = useFormatting();

  const docTypeLabel = getDocTypeLabel(pendingDocShare.docType, t);
  const location = pendingDocShare.projectLocation;
  const chipLabel = location
    ? `${docTypeLabel} • ${[location.postalCode, location.city]
        .filter(Boolean)
        .join(" ")}`
    : docTypeLabel;

  const content = (
    <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Avatar
            size="s"
            initials={pendingDocShare.sharingSystemOrganisationTitle.substring(
              0,
              2
            )}
          />
          <Typography variant="body2" sx={{ flexGrow: 1 }} noWrap>
            {pendingDocShare.sharingSystemOrganisationTitle}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {moment(pendingDocShare.createdAt).fromNow()}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          spacing={2}
          sx={{ alignItems: "baseline", justifyContent: "space-between" }}
        >
          <Typography variant="h4">{pendingDocShare.docTitle}</Typography>
          {pendingDocShare.netTotal != null && (
            <Stack
              direction="row"
              spacing={0.5}
              sx={{ alignItems: "baseline" }}
            >
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {t("net", { ns: "Dashboard" })}
              </Typography>
              <Typography variant="h4" noWrap>
                {getFormattedPrice(pendingDocShare.netTotal)}
              </Typography>
            </Stack>
          )}
        </Stack>
        <Stack direction="row">
          <Chip
            size="small"
            variant="outlined"
            color="success"
            icon={<AttachFileIcon />}
            label={chipLabel}
          />
        </Stack>
      </Stack>
    </CardContent>
  );

  return (
    <Card variant="outlined">
      {/* Accept stays outside the action area, so it never opens the preview. */}
      {/* The preview (`pendingDocSharePreview`) only resolves quotes. */}
      {pendingDocShare.docType === "QUOTE" ? (
        <CardActionArea onClick={onPreview}>{content}</CardActionArea>
      ) : (
        content
      )}
      <Divider sx={{ mx: 1 }} />
      <CardActions sx={{ justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={onAccept}
        >
          {t("Accept", { ns: "Global" })}
        </Button>
      </CardActions>
    </Card>
  );
}

function getDocTypeLabel(docType: DocType, t: TFunction<"Dashboard">) {
  switch (docType) {
    case "QUOTE":
      return t("Quote", { ns: "Dashboard" });
    case "REQUIREMENT":
      return t("Requirement", { ns: "Dashboard" });
    case "BUILDING":
      return t("Building", { ns: "Dashboard" });
    case "TEMPLATE":
      return t("Template", { ns: "Dashboard" });
    default:
      return assertNever(docType);
  }
}

function AcceptPendingDocShareModal({
  pendingDocShare,
  handleClose,
  handleAccepted,
}: {
  pendingDocShare: PendingDocSharesBox_PendingDocShareFragment;
  handleClose: () => void;
  handleAccepted: (
    projectId: string,
    clientCrmOrganisationId: string | null
  ) => Promise<void>;
}) {
  const { t } = useTranslate(["Dashboard"]);
  const { enqueueSnackbar } = useSnackbar();

  const clientActor = pendingDocShare.clientActor;

  const clientName = clientActor
    ? clientActor.companyName ||
      [clientActor.firstname, clientActor.familyname]
        .filter(Boolean)
        .join(" ") ||
      clientActor.email ||
      ""
    : "";

  // A CLIENT actor with a company name is a company contact; otherwise treat it
  // as an individual.
  const crmOrganisation =
    clientActor?.matchedCrmCompanyId != null && clientName
      ? { id: clientActor.matchedCrmCompanyId, title: clientName }
      : undefined;

  const initialClientPrefill: CrmCreateFormPrefill | undefined =
    clientActor && clientActor.matchedCrmCompanyId == null
      ? {
          companyName: clientActor.companyName ?? undefined,
          firstname: clientActor.firstname ?? undefined,
          familyname: clientActor.familyname ?? undefined,
          email: clientActor.email ?? undefined,
          contactType: clientActor.companyName ? "COMPANY" : "INDIVIDUAL",
        }
      : undefined;

  return (
    <CreateProjectModal
      title={t("Accept shared document", { ns: "Dashboard" })}
      pageName="Dashboard"
      desiredProjectInitialStatus="opportunity"
      crmOrganisation={crmOrganisation}
      initialClientPrefill={initialClientPrefill}
      handleClose={handleClose}
      handleComplete={async (projectId, crmOrganisationId) => {
        await handleAccepted(projectId, crmOrganisationId);
        enqueueSnackbar(t("Shared document accepted", { ns: "Dashboard" }));
      }}
    />
  );
}
